import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, User, Phone, Mail, Home, FileText, Send } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
export function ConsultationPage() {
  const {
    t
  } = useLanguage();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    lossType: '',
    lossDate: '',
    insuranceCompany: '',
    policyNumber: '',
    description: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const message = `${t.consult.waMsg}

*${t.consult.fields.fullName}:* ${form.fullName}
*${t.consult.fields.phone}:* ${form.phone}
*${t.consult.fields.email}:* ${form.email}
*${t.consult.fields.address}:* ${form.address}
*${t.consult.fields.lossType}:* ${form.lossType}
*${t.consult.fields.lossDate}:* ${form.lossDate}
*${t.consult.fields.insurance}:* ${form.insuranceCompany}
*${t.consult.fields.policy}:* ${form.policyNumber}
*${t.consult.fields.description}:* ${form.description}`;
    const encoded = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/13054170530?text=${encoded}`;
    window.open(whatsappUrl, '_blank');
  };
  return <div className="pt-20" data-id="element-338">
      {/* Hero */}
      <section className="relative bg-brand-dark py-20 overflow-hidden" data-id="element-339">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")'
      }} data-id="element-340" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/95 to-brand-dark/80" data-id="element-341" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-342">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-gold hover:text-white transition-colors mb-8 font-semibold" data-id="element-343">
            <ArrowLeft size={20} data-id="element-344" />
            {t.free.back}
          </Link>

          <motion.div {...fadeIn} data-id="element-345">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-red/10 border border-brand-red/20 mb-6" data-id="element-346">
              <MessageCircle className="text-brand-gold" size={20} data-id="element-347" />
              <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider" data-id="element-348">
                {t.consult.badge}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4" data-id="element-349">
              {t.consult.title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-350">
              {t.consult.desc}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 bg-white" data-id="element-351">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-352">
          <motion.div {...fadeIn} className="bg-brand-gray p-8 md:p-12 rounded-sm shadow-lg" data-id="element-353">
            <div className="flex items-center gap-3 mb-8" data-id="element-354">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center" data-id="element-355">
                <MessageCircle className="text-white" size={24} data-id="element-356" />
              </div>
              <div data-id="element-357">
                <h2 className="text-2xl font-bold text-brand-dark" data-id="element-358">
                  {t.consult.formTitle}
                </h2>
                <p className="text-gray-500 text-sm" data-id="element-359">
                  {t.consult.formSubtitle}
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-360">
              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-361">
                <div data-id="element-362">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2" data-id="element-363">
                    <User size={16} data-id="element-364" />
                    {t.consult.fields.fullName} *
                  </label>
                  <input type="text" name="fullName" required value={form.fullName} onChange={handleChange} className="w-full px-4 py-3 rounded-sm border border-gray-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20 outline-none transition-all" data-id="element-365" />
                </div>
                <div data-id="element-366">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2" data-id="element-367">
                    <Phone size={16} data-id="element-368" />
                    {t.consult.fields.phone} *
                  </label>
                  <input type="tel" name="phone" required value={form.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-sm border border-gray-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20 outline-none transition-all" data-id="element-369" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-370">
                <div data-id="element-371">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2" data-id="element-372">
                    <Mail size={16} data-id="element-373" />
                    {t.consult.fields.email}
                  </label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} className="w-full px-4 py-3 rounded-sm border border-gray-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20 outline-none transition-all" data-id="element-374" />
                </div>
                <div data-id="element-375">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2" data-id="element-376">
                    <Home size={16} data-id="element-377" />
                    {t.consult.fields.address} *
                  </label>
                  <input type="text" name="address" required value={form.address} onChange={handleChange} className="w-full px-4 py-3 rounded-sm border border-gray-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20 outline-none transition-all" data-id="element-378" />
                </div>
              </div>

              {/* Loss Info */}
              <div className="border-t border-gray-200 pt-6" data-id="element-379">
                <h3 className="text-lg font-bold text-brand-dark mb-4 flex items-center gap-2" data-id="element-380">
                  <FileText size={20} className="text-brand-red" data-id="element-381" />
                  {t.consult.lossSection}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-382">
                <div data-id="element-383">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-384">
                    {t.consult.fields.lossType} *
                  </label>
                  <select name="lossType" required value={form.lossType} onChange={handleChange} className="w-full px-4 py-3 rounded-sm border border-gray-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20 outline-none transition-all bg-white" data-id="element-385">
                    <option value="" data-id="element-386">{t.consult.selectOption}</option>
                    {t.consult.lossTypes.map((type, i) => <option key={i} value={type} data-id="element-387">
                        {type}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-388">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-389">
                    {t.consult.fields.lossDate} *
                  </label>
                  <input type="date" name="lossDate" required value={form.lossDate} onChange={handleChange} className="w-full px-4 py-3 rounded-sm border border-gray-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20 outline-none transition-all" data-id="element-390" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-391">
                <div data-id="element-392">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-393">
                    {t.consult.fields.insurance}
                  </label>
                  <input type="text" name="insuranceCompany" value={form.insuranceCompany} onChange={handleChange} className="w-full px-4 py-3 rounded-sm border border-gray-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20 outline-none transition-all" data-id="element-394" />
                </div>
                <div data-id="element-395">
                  <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-396">
                    {t.consult.fields.policy}
                  </label>
                  <input type="text" name="policyNumber" value={form.policyNumber} onChange={handleChange} className="w-full px-4 py-3 rounded-sm border border-gray-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20 outline-none transition-all" data-id="element-397" />
                </div>
              </div>

              <div data-id="element-398">
                <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-399">
                  {t.consult.fields.description}
                </label>
                <textarea name="description" rows={4} value={form.description} onChange={handleChange} className="w-full px-4 py-3 rounded-sm border border-gray-300 focus:border-brand-red focus:ring-2 focus:ring-brand-red/20 outline-none transition-all" data-id="element-400"></textarea>
              </div>

              <button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold text-lg py-4 rounded-sm transition-colors shadow-lg flex items-center justify-center gap-3" data-id="element-401">
                <Send size={22} data-id="element-402" />
                {t.consult.submit}
              </button>

              <p className="text-center text-gray-500 text-sm" data-id="element-403">
                {t.consult.note}
              </p>
            </form>
          </motion.div>
        </div>
      </section>
    </div>;
}